import {isTurnoverIncomeFsCode} from '@/adjustment/adjust/dialogs/utils/adjustment-helpers';

/**
 * Component type determined from the paper-registry code mapping.
 * Each type corresponds to a specific set of row fields in the detail payload.
 */
type ComponentType =
    | 'common'            // commonRow
    | 'bank'              // bankRow (+ commonRow)
    | 'commonTransaction' // commonRow (ignore relatedRows)
    | 'commonMaterialTest'// commonRow (ignore testRow, relatedRows)
    | 'commonInvestment'  // intangibleRows (legacy costRow fallback)
    | 'feesTransaction'   // feesRow (ignore relatedRows)
    | 'debtTransaction'   // debtRow (ignore relatedRows)
    | 'debtFeesTransaction' // debtFeeRow (merged debt/fees, ignore relatedRows)
    | 'inventories'       // commonRow (ignore testRow)
    | 'dueFromToDirectors'// dueFromToDirRow
    | 'gainLossExchange'  // gainExchange + lossExchange
    | 'gainLossDisposal'  // 10+ disposal row arrays
    | 'commissionPayment' // commissionPayments
    | 'withholdingTax'    // whtRows
    | 'donation'          // taxDeductible + nonTaxDeductible
    | 'purchases'         // purchases (ignore transactionTest)
    | 'rentalExpense'     // rows
    | 'salariesAllowance' // special: staffRows/workerRows/directorRows VM
    | 'interestIncome'    // bankInterestIncome + otherInterestIncome
    | 'salesIncome'       // salesIncome (ignore transactionTest)
    | 'bankLoan'          // bankLoans
    | 'vehicleLoan'       // vehicleLoans
    | 'interestSummary'   // interestSummary (read-only)
    | 'tax'               // commonRow
    | 'unknown';

/**
 * Infer component type from a schedule code by checking which
 * dynamic component the paper-registry maps it to.
 * Falls back to pattern matching on the code string.
 */
function inferComponentType(code: string): ComponentType {
    if (!code) return 'unknown';
    const upper = code.toUpperCase();

    // --- Explicit code-prefix patterns (most reliable) ---

    // Tax
    if (upper === 'BS-CA-TAXR' || upper === 'BS-CL-TAXP' || upper === 'PL-TAX') return 'tax';

    // Loan
    if (upper === 'BS-CL-BLS' || upper === 'BS-NCL-BLS') return 'bankLoan';
    if (upper === 'BS-CL-OFL' || upper === 'BS-NCL-OFL') return 'vehicleLoan';
    if (upper === 'PL-FC-LI') return 'interestSummary';

    // Bank (BankWp)
    if (upper === 'BS-CA-CASH' || upper === 'BS-CL-BOD') return 'bank';

    // DueFromToDirectors
    if (upper === 'BS-CA-DIR' || upper === 'BS-CL-DTD') return 'dueFromToDirectors';

    // Inventories
    if (upper === 'BS-CA-INV' || upper === 'PL-COT-OI' || upper === 'PL-COT-CI') return 'inventories';

    // CommonInvestment
    if (upper === 'BS-NCA-ISUB' || upper === 'BS-NCA-IASS' || upper === 'BS-CA-IOT') return 'commonInvestment';

    // GainLossExchange
    if (upper === 'PL-OI-EXG' || upper === 'PL-AE-EXL') return 'gainLossExchange';

    // GainLossDisposal
    if (upper === 'PL-OI-GOD' || upper === 'PL-AE-LOD') return 'gainLossDisposal';

    // SalesIncome / Service Income（SalesIncomeWp）
    if (isTurnoverIncomeFsCode(upper)) return 'salesIncome';

    // InterestIncome
    if (upper === 'PL-OI-II' || upper === 'PL-OI-SFG') return 'interestIncome';

    // Purchases
    if (upper === 'PL-COT-PUR') return 'purchases';

    // CommissionPayment
    if (upper === 'PL-AE-COM') return 'commissionPayment';

    // Withholding Tax (R14)
    if (upper === 'PL-AE-WHT') return 'withholdingTax';

    // Donation
    if (upper === 'PL-AE-DON') return 'donation';

    // RentalExpense / Rental Income
    if (upper === 'PL-AE-REN' || upper === 'PL-TURN-RI') return 'rentalExpense';

    // SalariesAllowance
    if (upper === 'PL-AE-SAL' || upper === 'PL-AE-WAG' || upper === 'PL-AE-DR' ||
        upper === 'PL-AE-DQ' || upper === 'PL-AE-MPF') return 'salariesAllowance';

    // DebtFeesTransaction (merged)
    if (upper === 'PL-AE-CF' || upper === 'PL-AE-LPF' || upper === 'PL-AE-MGT' ||
        upper === 'PL-COT-SUB' || upper === 'PL-AE-SUE' ||
        upper === 'PL-AE-BD' || upper === 'PL-AE-PDD') return 'debtFeesTransaction';

    // CommonTransaction (various PL-OI and PL-COT codes)
    if (upper === 'PL-OI-DI' || upper === 'PL-OI-ALI' || upper === 'PL-OI-SI' ||
        upper === 'PL-OI-II' || upper === 'PL-COT-ROY' || upper === 'PL-COT-OCT' ||
        upper === 'PL-AE-IPP') return 'commonTransaction';

    // CommonMaterialTest — the bulk of PL-AE-* codes
    if (upper.startsWith('PL-AE-')) return 'commonMaterialTest';

    // Impairment Loss (uses CommonWp → commonRow)
    if (upper === 'PL-AE-IML') return 'common';

    // Default: common (CommonWp)
    return 'common';
}

// All disposal row field names in GainLossDisposalWorkingPaper
const DISPOSAL_ROW_FIELDS = [
    'locationOfPropertyDisRow',
    'commercialAllowanceDisRow',
    'industrialAllowanceDisRow',
    'furnitureFixtureDisRow',
    'computerHardSoftDisRow',
    'fullPayMotorDisRow',
    'hirePurchaseMotorDisRow',
    'machineryDisRow',
    'liftEscalatorDisRow',
    'equipmentDisRow',
    'subsidiaryRow',
    'associateRow',
    'intangibleRow',
    'taxiLicenseDisRow',
    'fullPayTaxiCabDisRow',
    'hirePurchaseTaxiCabDisRow',
    'trustsRow',
] as const;

/**
 * Extract adjustment-participating rows from component detail data.
 * 
 * @param code - Schedule code (e.g., 'BS-CA-CASH')
 * @param detail - The component detail object from adjustDraftStore
 * @returns Flat array of row objects that participate in adjustment
 */
export function extractAdjustmentRows(code: string, detail: any): any[] {
    if (!detail || typeof detail !== 'object') return [];

    const type = inferComponentType(code);

    switch (type) {
        case 'common':
        case 'commonTransaction':
        case 'commonMaterialTest':
        case 'inventories':
        case 'tax':
            return asArray(detail.commonRow);

        case 'bank':
            return [...asArray(detail.bankRow), ...asArray(detail.commonRow)];

        case 'commonInvestment':
            return asArray(detail.intangibleRows).length > 0
                ? asArray(detail.intangibleRows)
                : asArray(detail.costRow);

        case 'feesTransaction':
            return asArray(detail.feesRow);

        case 'debtTransaction':
            return asArray(detail.debtRow);

        case 'debtFeesTransaction':
            return asArray(detail.debtFeeRow);

        case 'dueFromToDirectors':
            return asArray(detail.dueFromToDirRow);

        case 'gainLossExchange':
            return [...asArray(detail.gainExchange), ...asArray(detail.lossExchange)];

        case 'gainLossDisposal': {
            const rows: any[] = [];
            for (const field of DISPOSAL_ROW_FIELDS) {
                rows.push(...asArray(detail[field]));
            }
            return rows;
        }

        case 'commissionPayment':
            return asArray(detail.commissionPayments);

        case 'withholdingTax':
            return asArray(detail.whtRows);

        case 'donation':
            return [...asArray(detail.taxDeductible), ...asArray(detail.nonTaxDeductible)];

        case 'purchases':
            return asArray(detail.purchases);

        case 'rentalExpense':
            return asArray(detail.rows);

        case 'salariesAllowance':
            // SalariesAllowance uses VM row structure; extract all row groups
            return [
                ...asArray(detail.staffRows),
                ...asArray(detail.workerRows),
                ...asArray(detail.directorRows),
                ...asArray(detail.dirQuarterRows),
            ];

        case 'interestIncome':
            return [...asArray(detail.bankInterestIncome), ...asArray(detail.otherInterestIncome)];

        case 'salesIncome':
            return asArray(detail.salesIncome);

        case 'bankLoan':
            return asArray(detail.bankLoans);

        case 'vehicleLoan':
            return asArray(detail.vehicleLoans);

        case 'interestSummary':
            // Read-only aggregate — no editable adjustment rows
            return [];

        case 'unknown':
        default:
            // Fallback: try common fields
            return asArray(detail.commonRow) || asArray(detail.rows) || [];
    }
}

/**
 * Get the component type string for a given schedule code.
 * Useful for debugging and logging.
 */
export function getComponentType(code: string): ComponentType {
    return inferComponentType(code);
}

/** Safely coerce a value to an array */
function asArray(val: any): any[] {
    if (!val) return [];
    return Array.isArray(val) ? val : [val];
}
